import { httpClient } from '@/api/httpClient';
import {
  resolveAttachmentFilename,
  type DownloadedFile,
} from '@/api/fileDownload';
import {
  mapApiResourceToReportsSummary,
  mapApiResourceToTreatmentsDeliveredRow,
  mapApiResourceToVisitsByStatusRow,
  mapApiResourceToVisitsByTypeRow,
  type ReportsSummary,
  type ReportsSummaryApiResource,
  type TreatmentsDeliveredRow,
  type TreatmentsDeliveredRowApiResource,
  type VisitsByStatusRow,
  type VisitsByStatusRowApiResource,
  type VisitsByTypeRow,
  type VisitsByTypeRowApiResource,
} from './reportsApi.types';

export interface ReportDateRangeParameters {
  dateFrom: string | null;
  dateTo: string | null;
}

function buildDateRangeParams(dateRange: ReportDateRangeParameters): Record<string, string> {
  const params: Record<string, string> = {};
  if (dateRange.dateFrom !== null) {
    params.date_from = dateRange.dateFrom;
  }
  if (dateRange.dateTo !== null) {
    params.date_to = dateRange.dateTo;
  }
  return params;
}

const REPORTS_BASE_PATH = '/me/workspace/reports';

const DEFAULT_REPORT_FILENAME = 'clinic-report.pdf';

export async function downloadReportPdf(
  dateRange: ReportDateRangeParameters
): Promise<DownloadedFile> {
  const response = await httpClient.get<Blob>(REPORTS_BASE_PATH + '/pdf', {
    params: buildDateRangeParams(dateRange),
    responseType: 'blob',
  });
  return {
    blob: response.data,
    filename: resolveAttachmentFilename(
      response.headers['content-disposition'],
      DEFAULT_REPORT_FILENAME
    ),
  };
}

export async function fetchReportsSummary(
  dateRange: ReportDateRangeParameters
): Promise<ReportsSummary> {
  const response = await httpClient.get<ReportsSummaryApiResource>(
    REPORTS_BASE_PATH + '/summary',
    { params: buildDateRangeParams(dateRange) }
  );
  return mapApiResourceToReportsSummary(response.data);
}

export async function fetchVisitsByType(
  dateRange: ReportDateRangeParameters
): Promise<VisitsByTypeRow[]> {
  const response = await httpClient.get<VisitsByTypeRowApiResource[]>(
    REPORTS_BASE_PATH + '/visits-by-type',
    { params: buildDateRangeParams(dateRange) }
  );
  return response.data.map(mapApiResourceToVisitsByTypeRow);
}

export async function fetchVisitsByStatus(
  dateRange: ReportDateRangeParameters
): Promise<VisitsByStatusRow[]> {
  const response = await httpClient.get<VisitsByStatusRowApiResource[]>(
    REPORTS_BASE_PATH + '/visits-by-status',
    { params: buildDateRangeParams(dateRange) }
  );
  return response.data.map(mapApiResourceToVisitsByStatusRow);
}

export async function fetchTreatmentsDelivered(
  dateRange: ReportDateRangeParameters
): Promise<TreatmentsDeliveredRow[]> {
  const response = await httpClient.get<TreatmentsDeliveredRowApiResource[]>(
    REPORTS_BASE_PATH + '/treatments-delivered',
    { params: buildDateRangeParams(dateRange) }
  );
  return response.data.map(mapApiResourceToTreatmentsDeliveredRow);
}
